import { useState, useEffect, memo, SyntheticEvent, ChangeEvent, ChangeEventHandler, FunctionComponent } from 'react';
import { Svg } from '../common/Svg.js';
import { LightBoxWrapper } from '../common/LightBoxWrapper.js';
import { submitEmailContactSignup } from '../common/submitEmailContactSignup.js';
import { onClose as updateLocalStorageOnClose, onSignup as updateLocalStorageOnSignup } from './emailSignupLocalStorage.ts';
import { InputValidatedOnBlur } from './InputValidatedOnBlur.tsx';
import { isValidZip } from '../utils/addressUtils.ts';

const TEACHER_REG_URL = baseURL + 'teachers';

/**
 * What we show in the LeadGenWidget after a user successfully signs up.
 */
const SuccessPanel = () => {
    // TODO this is a draft success-panel to be handed off to Design to finish. See WS-34760.
    return (
        <div className="lead-gen-signup-success">
            <h2>Thank you for signing up!</h2>

            <div>Check your inbox to confirm your email.</div>
        </div>
    );
};

const isBlank = (str: string) => {
    return !str || str.trim().length === 0;
};
const validateName = (name: string) => {
    return !isBlank(name);
};
const validateEmail = (email: string) => {
    return !isBlank(email) && email.includes('@');
};
// Note: because this widget shares an endpoint with EmailContactSignUpWidget (which doesn't have a name or zip field),
// the serverside validation won't match this clientside validation - the endpoint will allow submissions without a name
// or zip. If we remove EmailContactSignUpWidget entirely, we should add corresponding serverside validation.
const allowSubmit = (name: string, email: string, zip: string) => {
    return validateName(name) && validateEmail(email) && isValidZip(zip);
};
const noopSubmitHandler = (e: SyntheticEvent) => {
    e.preventDefault();
};

interface LeadGenFormProps {
    onSubmit: (e: SyntheticEvent) => void;
    name: string;
    onNameChange: ChangeEventHandler;
    email: string;
    onEmailChange: ChangeEventHandler;
    zip: string;
    onZipChange: ChangeEventHandler;
    errorMessage: string | null;
}
/**
 * The normal contents of a LeadGenWidget: a form to sign up for emails.
 */
const LeadGenForm = ({ onSubmit: onSubmitProp, name, onNameChange, email, onEmailChange, zip, onZipChange, errorMessage }: LeadGenFormProps) => {
    let errorPanel = null;
    if (errorMessage) {
        errorPanel = <div className="alert-message">{errorMessage}</div>;
    }

    const canSubmit = allowSubmit(name, email, zip);
    const onSubmit = canSubmit ? onSubmitProp : noopSubmitHandler;

    return (
        <div className="lead-gen-modal">
            <form className="form-content" onSubmit={onSubmit}>
                <h3>Get a $25 promo code!</h3>
                <p>New email subscribers can unlock a $25 promo code when signing up for local project updates today, while funds last.</p>

                {errorPanel}

                <div className="form-redux search-area">
                    <InputValidatedOnBlur
                        id="lead-gen-name"
                        className="search-box"
                        validator={validateName}
                        errorCopy={'Please enter your full name.'}
                        type="text"
                        placeholder="Full name"
                        spellCheck="false"
                        aria-autocomplete="list"
                        value={name}
                        onChange={onNameChange}
                    />

                    <InputValidatedOnBlur
                        id="lead-gen-email"
                        className="search-box"
                        validator={validateEmail}
                        errorCopy={'Please enter a valid email address.'}
                        type="email"
                        placeholder="Email"
                        spellCheck="false"
                        aria-autocomplete="list"
                        value={email}
                        onChange={onEmailChange}
                    />

                    <InputValidatedOnBlur
                        className="search-box"
                        validator={isValidZip}
                        errorCopy={'Please enter a valid ZIP code.'}
                        id="lead-gen-zip"
                        type="text"
                        placeholder="Zip code"
                        spellCheck="false"
                        aria-autocomplete="list"
                        value={zip}
                        onChange={onZipChange}
                    />
                </div>

                <button
                    id="lead-gen-sign-up"
                    className="button-primary full-width heap-lead-gen-sign-up"
                    disabled={!canSubmit}
                >Sign up
                </button>
                <span className="text-discreet teacher-callout">
                    &#x1f34e; Are you a teacher? <a href={TEACHER_REG_URL}
                        className="teacher-sign-up-link heap-lead-gen-teacher-sign-up">Register here and get funded!</a>
                </span>
            </form>
            <div className="teacher-image-content">
                <div className="teacher-credit">
                    <a href="/classroom/mrs-bacchus" target="_blank" className="teacher-name">Mrs. Bacchus</a>
                    <p className="teacher-title text-discreet">PS 245, Brooklyn, NY</p>
                </div>
            </div>
        </div>
    );
};

interface MaybeResponse {
    data?: {
        success: boolean;
        message: string;
    };
}

interface LeadGenWidgetProps {
    minimizedLeadGenEnabled: boolean;
    // Called when the user closes the main lead gen form.
    manualCloseHandler?: () => void;
    // Called when the user successfully submits the main lead gen form.
    successfulSubmitHandler?: () => void;
    // If true, put the widget in a lightbox with LightBoxWrapper. Defaults to true in the only caller, but must be
    // marked as optional because TypeScript itself can't know that.
    useLightbox?: boolean;
}

/**
 * A tool for signing up to receive our emails. The post-2024 version of emailContactSignupWidget.js.
 */
export let LeadGenWidget: FunctionComponent<LeadGenWidgetProps> = ({ minimizedLeadGenEnabled, manualCloseHandler, successfulSubmitHandler, useLightbox = true }) => {
    const [open, setOpen] = useState(true); // TODO add optional prop to set open-status?
    const [showPostSuccess, setShowPostSuccess] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [zip, setZip] = useState('');
    // Set if submission fails.
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        window?.dcAnalytics?.track('Lead gen opened', {});
    }, []); // empty deps to run once on mount

    if (!open) {
        return null;
    }

    const close = () => {
        setOpen(false);
    };
    const manualClose = () => {
        manualCloseHandler?.();

        updateLocalStorageOnClose(minimizedLeadGenEnabled);
        close();
    };
    const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        // The internet claims that using `currentTarget` instead of `target` lets you avoid casting here, but that's
        // not true for our current version(s).
        setName((event.target).value);
    };
    const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEmail((event.target).value);
    };
    const onZipChange = (event: ChangeEvent<HTMLInputElement>) => {
        setZip((event.target).value);
    };

    const onSuccess = () => {
        updateLocalStorageOnSignup();

        successfulSubmitHandler?.();

        setShowPostSuccess(true);
    };
    /**
     * @param xhrOrResponse Either a jQuery response-object or the data returned by the server. (The endpoint this hits
     * uses our AjaxUtils conventions. Which means it'll return a 200 but with `success: false` in response to invalid
     * input. So this handler can be called by either $.ajax.error (in which case it's a jqXhr) or by $.ajax.success (in
     * which case it's a response).
     */
    const onFailure = (xhrOrResponse?: MaybeResponse) => {
        if (xhrOrResponse && xhrOrResponse.data && !xhrOrResponse.data.success) {
            // Server rejected the request with a message; show it
            setErrorMessage(xhrOrResponse.data.message);
        } else {
            // Probably a 5xx or networking failure
            setErrorMessage('Error - Please refresh the page and try again.');
        }
    };
    const onSubmit = (event: SyntheticEvent) => {
        event.preventDefault();
        submitEmailContactSignup(name, zip, email, onSuccess, onFailure);
    };

    let contents;
    if (showPostSuccess) {
        contents = <SuccessPanel />;
    } else {
        contents = (
            <LeadGenForm
                zip={zip}
                onNameChange={onNameChange}
                onSubmit={onSubmit}
                name={name}
                onEmailChange={onEmailChange}
                onZipChange={onZipChange}
                email={email}
                errorMessage={errorMessage}
            />
        );
    }

    if (useLightbox) {
        return (
            <LightBoxWrapper unMountHandler={manualClose} frameless={true} fixed={true}>
                <div className="lead-gen-content">
                    <button tabIndex={0} className="circular-close" aria-label="close" onClick={manualClose}>
                        <Svg icon={'delete'} additionalClasses='is-button' attributes={{ role: 'presentation' }}/>
                    </button>

                    {contents}
                </div>
            </LightBoxWrapper>
        );
    } else {
        return contents;
    }
};
LeadGenWidget = memo(LeadGenWidget);
